// Darstellungsstil der Verrohrungskanten (Stromtypen) im Anlagenschema.
//
// Der plant-catalog kennt die Stromtypen samt Farbe/Strichstärke/Strichart. Für die
// React-Flow-Kante brauchen wir konkrete Farbwerte (kein CSS-var-Umweg, damit die Kante auch im
// SVG-Marker sauber einfärbt). Die Farben folgen dem Blackfort-CI/der Prozessleittechnik-
// Konvention (Wasser blau, Gas gelb, Luft cyan, Wärme rot, Schlamm braun) und setzen sie für die
// Industriedomänen fort: Dampf orange, Rauchgas grau, Feststoff/Schüttgut ocker, Produktstrom
// grün, Chemikalie violett. Die Wärmewende-Domänen bringen drei weitere mit: Kaltnetz/Sole
// türkis, Kälteträger eisblau, elektrische Leistung bernstein. Sole ist bewusst nicht rot
// eingefärbt — ein Anergienetz führt 15 °C, und wer es in der Leitwarte für einen
// Fernwärme-Vorlauf hält, liest die ganze Anlage falsch.

export interface StreamStyle {
  color: string;
  width: number;
  dash?: string; // SVG stroke-dasharray
  label_de: string;
  label_en: string;
}

// Bekannte Stromtypen (IDs deckungsgleich mit plant-catalog Stream/STREAM_TYPES).
export const STREAM_STYLES: Record<string, StreamStyle> = {
  wasser_abwasser: { color: '#3B82F6', width: 2, label_de: 'Wasser / Abwasser', label_en: 'Water / wastewater' },
  gas: { color: '#C49B18', width: 2, label_de: 'Gas', label_en: 'Gas' },
  luft: { color: '#38BDF8', width: 1.5, dash: '6 4', label_de: 'Luft / Prozessluft', label_en: 'Air / process air' },
  waerme: { color: '#EF4444', width: 2, dash: '2 3', label_de: 'Wärme', label_en: 'Heat' },
  substrat_schlamm: { color: '#A16207', width: 4, label_de: 'Substrat / Schlamm', label_en: 'Substrate / sludge' },
  dampf: { color: '#FB923C', width: 3, dash: '2 3', label_de: 'Dampf', label_en: 'Steam' },
  rauchgas: { color: '#94A3B8', width: 3, dash: '6 4', label_de: 'Rauchgas / Abgas', label_en: 'Flue gas' },
  feststoff: { color: '#B45309', width: 4, label_de: 'Feststoff / Schüttgut', label_en: 'Solids / bulk material' },
  prozessfluid: { color: '#34D399', width: 3, label_de: 'Prozessfluid / Produkt', label_en: 'Process fluid / product' },
  chemikalie: { color: '#C084FC', width: 1.5, dash: '2 3', label_de: 'Chemikalie / Dosiermedium', label_en: 'Chemical / dosing medium' },
  sole_kaltnetz: { color: '#2DD4BF', width: 3, label_de: 'Kaltnetz / Sole (Anergie)', label_en: 'Cold network / brine (anergy)' },
  kaeltetraeger: { color: '#7DD3FC', width: 2, label_de: 'Kälteträger / Kaltwasser', label_en: 'Coolant / chilled water' },
  strom: { color: '#FBBF24', width: 2, dash: '6 4', label_de: 'Elektrische Leistung', label_en: 'Electric power' },
};

// Neutraler Stil für unbekannte/fehlende Stromtypen (dezente Border-Farbe).
export const DEFAULT_STREAM_STYLE: StreamStyle = {
  color: '#7A8FAF',
  width: 2,
  label_de: 'Rohrleitung',
  label_en: 'Pipe',
};

export function streamStyle(stream: string | undefined): StreamStyle {
  if (stream && stream in STREAM_STYLES) return STREAM_STYLES[stream];
  return DEFAULT_STREAM_STYLE;
}

// Die Attribute einer stromtyp-gestylten Verrohrungskante (ohne React-Flow-Typabhängigkeit,
// damit sie unabhängig testbar sind): Handles (pipe-out → pipe-in), Farbe/Strichart nach Medium,
// Richtungspfeil, dezentes Label (lesbare Medienbezeichnung). Der Stromtyp-Schlüssel bleibt in
// data.stream erhalten (Round-Trip beim Speichern).
export function schematicEdgeAttrs(
  edge: { id?: string; from: string; to: string; stream?: string },
  index: number,
  lang: 'de' | 'en',
): Record<string, unknown> {
  const st = streamStyle(edge.stream);
  const label = edge.stream ? (lang === 'de' ? st.label_de : st.label_en) : undefined;
  return {
    id: edge.id ?? `sch-e-${index}`,
    source: edge.from,
    target: edge.to,
    sourceHandle: 'pipe-out',
    targetHandle: 'pipe-in',
    ...(label ? { label } : {}),
    ...(edge.stream ? { data: { stream: edge.stream } } : {}),
    style: { stroke: st.color, strokeWidth: st.width, ...(st.dash ? { strokeDasharray: st.dash } : {}) },
    markerEnd: { type: 'arrowclosed', color: st.color, width: 16, height: 16 },
    labelStyle: { fill: '#7A8FAF', fontSize: 10 },
    labelBgStyle: { fill: '#0E2246', fillOpacity: 0.85 },
    labelShowBg: true,
  };
}
